import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, IconButton } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { motion } from "framer-motion";
import styled from "styled-components";
import tw from "twin.macro";
import { useTranslation } from "react-i18next";
import { Typography, Card, CardContent, Link } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts.js";
import { ReactComponent as ChevronDownIcon } from "feather-icons/dist/icons/chevron-down.svg";
import { ReactComponent as SvgDecoratorBlob1 } from "images/svg-decorator-blob-7.svg";
import { ReactComponent as SvgDecoratorBlob2 } from "images/svg-decorator-blob-8.svg";
import BgTitle from "components/title/BgTitle";

const Description = tw.p`text-xs md:text-sm text-center text-gray-600 mt-2 max-w-[500px]`;

const Column = tw.div`flex flex-col items-center`;
const FAQSContainer = tw.dl`mt-8 md:mt-12 max-w-4xl relative`;

const FAQ = tw.div`cursor-pointer select-none mt-4 px-6 py-4 rounded-lg bg-gray-200 hover:bg-gray-300 transition duration-300`;
const Question = tw.dt`flex justify-between items-center`;
const QuestionText = tw.span`text-sm md:text-base font-semibold text-gray-800`;
const QuestionToggleIcon = motion(styled.span`
  ${tw`ml-2 transition duration-300`}
  svg {
    ${tw`w-5 h-5`}
  }
`);
const Answer = motion(
  tw.dd`text-xs md:text-sm leading-relaxed text-gray-700 mt-2`
);

const ContactCard = styled(Card)`
  ${tw`mt-6 cursor-pointer transition-all duration-300 hover:shadow-xl`}
`;

const DecoratorBlob1 = styled(SvgDecoratorBlob1)`
  ${tw`pointer-events-none -z-20 absolute right-0 top-0 h-40 w-40 opacity-10 transform translate-x-2/3 -translate-y-12`}
`;
const DecoratorBlob2 = styled(SvgDecoratorBlob2)`
  ${tw`pointer-events-none -z-20 absolute left-0 bottom-0 h-48 w-48 opacity-10 transform -translate-x-2/3`}
`;

// Add this styled component for the header container
const HeaderContainer = styled(Box)`
  ${tw`flex items-center justify-between cursor-pointer`}
  position: relative;
  padding-right: 40px;
`;

const NavigationButton = styled(IconButton)`
  ${tw`absolute right-0`}
  color: #0c4d96;
`;

export default ({ isXs = false }) => {
  const { t } = useTranslation();
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(null);
  const navigate = useNavigate();

  const handleNavigateToRecruitment = () => {
    navigate("/recruitment");
  };

  const toggleQuestion = (index) => {
    setActiveQuestionIndex(activeQuestionIndex === index ? null : index);
  };

  return (
    <Container>
      <ContentWithPaddingXl>
        <Column>
          <motion.div
            whileTap={{ scale: 0.98 }}
            onClick={handleNavigateToRecruitment}
          >
            <HeaderContainer>
              <BgTitle text={t("recruitment.heading")} isCenter={true} />
              <NavigationButton
                component={motion.button}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                size="medium"
              >
                <ArrowForwardIcon />
              </NavigationButton>
            </HeaderContainer>
          </motion.div>

          {/* Description Box */}
          <Box
            sx={{
              mt: 2,
              mb: -2,
              borderLeft: "4px solid",
              borderColor: "primary.main",
              p: 1,
              bgcolor: "#f5f5f5",
            }}
          >
            <Description>{t("recruitment.message")}</Description>
          </Box>

          {/* FAQs */}
          <FAQSContainer>
            {/* FAQ 1 */}
            <FAQ onClick={() => toggleQuestion(0)}>
              <Question>
                <QuestionText>{t("recruitment.SE_title")}</QuestionText>
                <QuestionToggleIcon
                  variants={{
                    collapsed: { rotate: 0 },
                    open: { rotate: -180 },
                  }}
                  initial="collapsed"
                  animate={activeQuestionIndex === 0 ? "open" : "collapsed"}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronDownIcon style={{ color: "black" }} />
                </QuestionToggleIcon>
              </Question>
              <Answer
                variants={{
                  open: { opacity: 1, height: "auto" },
                  collapsed: { opacity: 0, height: 0 },
                }}
                initial="collapsed"
                animate={activeQuestionIndex === 0 ? "open" : "collapsed"}
                transition={{ duration: 0.3 }}
              >
                {t("recruitment.SE_message")}
              </Answer>
            </FAQ>

            {/* FAQ 2 */}
            <FAQ onClick={() => toggleQuestion(1)}>
              <Question>
                <QuestionText>{t("recruitment.senior_SE_title")}</QuestionText>
                <QuestionToggleIcon
                  variants={{
                    collapsed: { rotate: 0 },
                    open: { rotate: -180 },
                  }}
                  initial="collapsed"
                  animate={activeQuestionIndex === 1 ? "open" : "collapsed"}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronDownIcon style={{ color: "black" }} />
                </QuestionToggleIcon>
              </Question>
              <Answer
                variants={{
                  open: { opacity: 1, height: "auto" },
                  collapsed: { opacity: 0, height: 0 },
                }}
                initial="collapsed"
                animate={activeQuestionIndex === 1 ? "open" : "collapsed"}
                transition={{ duration: 0.3 }}
              >
                {t("recruitment.senior_SE_message")}
              </Answer>
            </FAQ>

            {/* FAQ 3 - Updated to use a Card instead of an accordion */}
            <Box mt={1} mb={2}>
              <Link
                href="/recruitment"
                style={{ textDecoration: "none" }}
                onClick={(e) => e.stopPropagation()}
              >
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <ContactCard
                    variant="outlined"
                    sx={{
                      borderColor: "#e0e0e0",
                      "&:hover": {
                        borderColor: "#0c4d96",
                      },
                      backgroundColor: "#edf2f7",
                      borderRadius: "8px",
                    }}
                  >
                    <CardContent
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 3,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          mb={1}
                        >
                          {t("recruitment.contact_info")}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          backgroundColor: "white",
                          p: 1.5,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <OpenInNewIcon
                          style={{ width: 20, height: 20, color: "#0c4d96" }}
                        />
                      </Box>
                    </CardContent>
                  </ContactCard>
                </motion.div>
              </Link>
            </Box>
          </FAQSContainer>
        </Column>
      </ContentWithPaddingXl>
      <DecoratorBlob1 />
      <DecoratorBlob2 />
    </Container>
  );
};
